/** The read-only sections of the sheet. A section with nothing to say is left out, and a fact the list behind the sheet already shows on the person's row is not said twice. */
import type { ReactNode } from "react";
import type { LegislativeDistricts, MemberDetail } from "../../api/dashboard.types";
import type { PersonFull } from "../../api/person360.types";
import { href } from "../app/links";
import { cx, focusRing, SheetRows, SheetSection } from "../kit";
import { EditableContact } from "./EditContact";
import type { RowFacts } from "../screens/people/selection";
import { sentence } from "../screens/people/format";
import type { Case } from "./useCase";
import { fmtDate, localDay, sourceNote } from "./format";
import { PossibleMatches } from "./Namesakes";
import { AddressesSection } from "./Addresses";

type Row = readonly [label: string, value: ReactNode];


/** Rows that have a value. */
const present = (rows: readonly Row[]): Row[] => rows.filter(([, value]) => value);

const LINK = cx("link-line rounded-sm", focusRing);

/** A state status worth a line: anything but Active, sentence-cased ("Late renewal"). */
const statusWorthSaying = (status: string | null | undefined): string => (status && status !== "Active" ? sentence(status.toLowerCase()) : "");

/** member_since is a date, or the honest floor ("≤2023") when the true join date is not recoverable: that is shown as it came. */
const sinceText = (value: string | null): string => (value && /^\d{4}-\d{2}-\d{2}/.test(value) ? fmtDate(value) : (value ?? ""));

/** What the case adds to the person: why a held person is held, what a payment problem has behind it. */
export function CaseSection({ person, kase }: { person: PersonFull; kase: Case }) {
  const { payments } = person;
  // The reason is under the name; these are the records it compared them with (#454).
  if (kase.held) return <PossibleMatches matches={kase.held.matches} />;
  if (kase.kind !== "payment") return null;
  const rows = present([
    ["Last paid", fmtDate(payments.last_paid_on)],
    ["Billing", payments.current_cadence ?? ""],
    ["Pending check", payments.pending_check_anomaly ? `${payments.pending_check_anomaly.days_pending} days` : ""],
  ]);
  return rows.length > 0 ? (
    <SheetSection title="Payment">
      <SheetRows rows={rows} />
    </SheetSection>
  ) : null;
}

export function Sections({ person, districts, addresses, facts, kase, listPending }: { person: PersonFull; districts: LegislativeDistricts | null; addresses: MemberDetail["addresses"]; facts: RowFacts | undefined; kase: Case; listPending: boolean }) {
  const { identity, standing, membership_journey: journey } = person;
  const phone = identity.phone.mobile ?? identity.phone.office;
  const listShowsDistrict = listPending || (!!facts?.district && facts.district !== "—");
  const change = kase.licence;
  // A licence change shows CE21's own date beside the state's; it may be opened from a list whose date is the state's (Today's), so the list's is not used then.
  const ce21Expires = fmtDate(identity.license_expires_on);
  // Otherwise the date is Keystone's (#635): the later of the state list's and CE21's, as every list shows it, with where it came from.
  const listExpires = !change && !kase.settling && facts?.expires && facts.expires !== "—" ? facts.expires : "";
  const keystoneExpires = listExpires || fmtDate(identity.license_expires_effective ?? identity.license_expires_on);
  const source = sourceNote(identity);
  const issueChanged = !!change?.changedFields.includes("issue");

  const contact = present([
    [
      "Email",
      identity.email ? (
        <a href={`mailto:${identity.email}`} className={LINK}>{identity.email}</a>
      ) : (
        identity.shares_email_with && (
          <span>
            Shares {identity.shares_email_with.email} with{" "}
            <a href={href(`/people?person=${identity.shares_email_with.id}`)} data-name className={LINK}>
              {identity.shares_email_with.name}
            </a>
          </span>
        )
      ),
    ],
    ["Phone", phone && <a href={`tel:${phone}`} className={cx(LINK, "normal-nums")}>{phone}</a>],
    ["District", listShowsDistrict ? "" : identity.districts.join(", ")],
    // Who added a person in Keystone, and the day (theirs, not UTC's) (#731).
    // Added from the state licence list (#635): said so, with the day.
    ["Added", identity.added_in_keystone ? [fmtDate(localDay(identity.added_in_keystone.at)), identity.added_in_keystone.from === "state_list" && "from the state list", identity.added_in_keystone.by && `by ${identity.added_in_keystone.by}`].filter(Boolean).join(" ") : ""],
  ]);
  const membership = present([
    ["Tier", standing.tier_detail],
    ["Since", sinceText(journey.member_since)],
    ["Ends", fmtDate(journey.current_membership_rows[0]?.current_end_date)],
  ]);
  // A licence change shows both sides: CE21's date and the state's.
  const licence = present(
    change
      ? [
          [issueChanged ? "Issued, CE21" : "Issued", fmtDate(identity.license_issued_on)],
          ["Issued, state", issueChanged ? fmtDate(change.stateIssueDate) : ""],
          ["Expires, CE21", ce21Expires],
          ["Expires, state", fmtDate(change.stateExpirationDate)],
          ["State status", statusWorthSaying(change.stateStatus)],
        ]
      : [
          ["Issued", fmtDate(identity.license_issued_on)],
          [
            "Expires",
            keystoneExpires !== "—" && source ? (
              <span className="inline-flex flex-col items-end gap-1.5">
                {keystoneExpires}
                <span className="text-meta font-normal leading-none text-muted-foreground">{source}</span>
              </span>
            ) : (
              keystoneExpires
            ),
          ],
          ["State status", statusWorthSaying(identity.license_state_status)],
        ],
  );

  return (
    <>
      {identity.editable ? (
        <EditableContact key={identity.id} identity={identity} rows={contact} />
      ) : (
        contact.length > 0 && (
          <SheetSection title="Contact">
            <SheetRows rows={contact} />
          </SheetSection>
        )
      )}
      <AddressesSection addresses={addresses} onFile={districts} />
      {membership.length > 0 && (
        <SheetSection title="Membership">
          <SheetRows rows={membership} />
        </SheetSection>
      )}
      {licence.length > 0 && (
        <SheetSection title="Licence">
          <SheetRows rows={licence} />
        </SheetSection>
      )}
    </>
  );
}
